import React, { useMemo, useState } from 'react';
import { Clock } from 'lucide-react';
import {
  SCHEDULE_COMMENT_PRESETS,
  toDatetimeLocalValue,
} from '../../utils/scheduledComments';
import './CommentScheduleControls.css';

interface Props {
  disabled?: boolean;
  onSchedule: (when: Date) => void | Promise<void>;
}

const CommentScheduleControls: React.FC<Props> = ({ disabled, onSchedule }) => {
  const [open, setOpen] = useState(false);
  const [customLocal, setCustomLocal] = useState(() =>
    toDatetimeLocalValue(SCHEDULE_COMMENT_PRESETS[1]?.resolveAt() || new Date(Date.now() + 3600000)),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const preview = useMemo(() => {
    const d = new Date(customLocal);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }, [customLocal]);

  const run = async (when: Date) => {
    setSaving(true);
    setError(null);
    try {
      await onSchedule(when);
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="comment-schedule">
      <button
        type="button"
        className="comment-schedule-btn"
        title="Schedule send"
        aria-label="Schedule send"
        aria-expanded={open}
        disabled={disabled || saving}
        onClick={() => setOpen((v) => !v)}
      >
        <Clock size={14} aria-hidden />
        <span>Schedule</span>
      </button>
      {open ? (
        <div className="comment-schedule-popover" role="dialog" aria-label="Schedule comment">
          <div className="comment-schedule-presets">
            {SCHEDULE_COMMENT_PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                className="comment-schedule-preset"
                disabled={saving}
                onClick={() => void run(p.resolveAt())}
              >
                {p.label}
              </button>
            ))}
          </div>
          <label className="comment-schedule-custom">
            <span>Custom (local time)</span>
            <input
              type="datetime-local"
              value={customLocal}
              onChange={(e) => setCustomLocal(e.target.value)}
              disabled={saving}
            />
          </label>
          <div className="comment-schedule-footer">
            <span className="comment-schedule-preview">{preview}</span>
            <button
              type="button"
              className="comment-schedule-confirm"
              disabled={saving}
              onClick={() => {
                const d = new Date(customLocal);
                if (Number.isNaN(d.getTime())) {
                  setError('Pick a valid date and time.');
                  return;
                }
                void run(d);
              }}
            >
              {saving ? 'Scheduling…' : 'Schedule send'}
            </button>
          </div>
          {error ? <p className="comment-schedule-error">{error}</p> : null}
        </div>
      ) : null}
    </div>
  );
};

export default CommentScheduleControls;
